import {
  AccAddress,
  ValAddress,
  UnbondingDelegation,
  Coin,
  Coins,
} from '../../../core';
import { BaseAPI } from './BaseAPI';
import { Delegation } from '../../../core/staking/Delegation';
import { Validator } from '../../../core/staking/Validator';
import { Redelegation } from '../../../core/staking/Redelegation';
import { Denom } from '../../../core/Denom';

export interface StakingParams {
  unbonding_time: number;
  max_validators: number;
  max_entries: number;
  bond_denom: Denom;
}

export namespace StakingParams {
  export interface Data {
    unbonding_time: string;
    max_validators: number;
    max_entries: number;
    bond_denom: Denom;
  }
}

export interface StakingPool {
  bonded_tokens: Coin;
  not_bonded_tokens: Coin;
}

export namespace StakingPool {
  export interface Data {
    bonded_tokens: string;
    not_bonded_tokens: string;
  }
}

export class StakingAPI extends BaseAPI {
  public async delegations(
    delegator?: AccAddress,
    validator?: ValAddress
  ): Promise<Delegation[]> {
    if (delegator !== undefined && validator !== undefined) {
      return this.c
        .get<Delegation.Data>(
          `/staking/delegators/${delegator}/delegations/${validator}`
        )
        .then(data => [Delegation.fromData(data.result)]);
    } else if (delegator !== undefined) {
      return this.c
        .get<Delegation.Data[]>(`/staking/delegators/${delegator}/delegations`)
        .then(data => data.result.map(Delegation.fromData));
    } else if (validator !== undefined) {
      return this.c
        .get<Delegation.Data[]>(`/staking/validators/${validator}/delegations`)
        .then(data => data.result.map(Delegation.fromData));
    } else {
      throw new TypeError(
        'arguments delegator and validator cannot both be empty'
      );
    }
  }

  public async delegation(
    delegator: AccAddress,
    validator: ValAddress
  ): Promise<Delegation> {
    return this.delegations(delegator, validator).then(delgs => delgs[0]);
  }

  public async unbondingDelegations(
    delegator: AccAddress,
    validator: ValAddress
  ): Promise<UnbondingDelegation[]> {
    if (delegator !== undefined && validator !== undefined) {
      return this.c
        .get<UnbondingDelegation.Data>(
          `/staking/delegators/${delegator}/unbonding_delegations/${validator}`
        )
        .then(data => [UnbondingDelegation.fromData(data.result)]);
    } else if (delegator !== undefined) {
      return this.c
        .get<UnbondingDelegation.Data[]>(
          `/staking/delegators/${delegator}/unbonding_delegations`
        )
        .then(data => data.result.map(UnbondingDelegation.fromData));
    } else if (validator !== undefined) {
      return this.c
        .get<UnbondingDelegation.Data[]>(
          `/staking/validators/${validator}/unbonding_delegations`
        )
        .then(data => data.result.map(UnbondingDelegation.fromData));
    } else {
      throw new TypeError(
        'arguments delegator and validator cannot both be empty'
      );
    }
  }

  public async unbondingDelegation(
    delegator: AccAddress,
    validator: ValAddress
  ): Promise<UnbondingDelegation> {
    return this.unbondingDelegations(delegator, validator).then(
      udelgs => udelgs[0]
    );
  }

  public async redelegations(
    delegator?: AccAddress,
    validatorSrc?: ValAddress,
    validatorDst?: ValAddress
  ): Promise<Redelegation[]> {
    let params = {
      delegator,
      validator_from: validatorSrc,
      validator_to: validatorDst,
    };
    return this.c
      .get<Redelegation.Data[]>(`/staking/redelegations`, params)
      .then(d => d.result.map(Redelegation.fromData));
  }

  public async bondedValidators(delegator: AccAddress): Promise<Validator[]> {
    return this.c
      .get<Validator.Data[]>(`/staking/delegators/${delegator}/validators`)
      .then(d => d.result.map(Validator.fromData));
  }

  public async validators(): Promise<Validator[]> {
    return this.c
      .get<Validator.Data[]>(`/staking/validators`)
      .then(d => d.result.map(Validator.fromData));
  }

  public async validator(validator: ValAddress) {
    return this.c
      .get<Validator.Data>(`/staking/validators/${validator}`)
      .then(d => Validator.fromData(d.result));
  }

  public async pool(): Promise<StakingPool> {
    return this.c
      .get<StakingPool.Data>(`/staking/pool`)
      .then(d => d.result)
      .then(d => ({
        bonded_tokens: new Coin(Denom.LUNA, d.bonded_tokens),
        not_bonded_tokens: new Coin(Denom.LUNA, d.not_bonded_tokens),
      }));
  }

  public async parameters(): Promise<StakingParams> {
    return this.c
      .get<StakingParams.Data>(`/staking/parameters`)
      .then(d => d.result)
      .then(d => ({
        unbonding_time: Number.parseInt(d.unbonding_time),
        max_validators: d.max_validators,
        max_entries: d.max_entries,
        bond_denom: d.bond_denom,
      }));
  }
}
